import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Users, Building2, Coins, Search, X, ArrowUp, ArrowDown,
  AlertCircle, FileText, FileSpreadsheet, Printer, Filter, SlidersHorizontal,
  Loader2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight,
} from "lucide-react";
import styles from "./InsuranceReport.module.css";
import { useToast } from "../../../context/ToastContext";
import { getInsuranceReport } from "../../../api/reports/useReportsApi";
import {
  formatEUR, formatReportDate, sortRows, downloadExcel, printHtml, STATUS_BADGE_KEYS,
} from "../reportUtils";

const EMPTY_SUMMARY = {
  totalPolicies: 0, totalTravelers: 0, totalCompanies: 0, totalRevenue: 0,
  confirmed: 0, pending: 0, cancelled: 0,
};

const INITIAL_CRITERIA = { dateFrom: "", dateTo: "", company: "", bookingStatus: "" };
const PAGE_SIZES = [25, 50, 100];

const KPI_META = [
  { key: "totalPolicies", label: "Policies", Icon: ShieldCheck, cls: "kpiBlue", fmt: (v) => v },
  { key: "totalTravelers", label: "Travellers", Icon: Users, cls: "kpiGreen", fmt: (v) => v.toLocaleString("en-US") },
  { key: "totalCompanies", label: "Insurance Companies", Icon: Building2, cls: "kpiPurple", fmt: (v) => v },
  { key: "totalRevenue", label: "Revenue", Icon: Coins, cls: "kpiOrange", fmt: (v) => formatEUR(v) },
];

const StatusBadge = ({ status }) => {
  const key = STATUS_BADGE_KEYS[status] || "pending";
  const cls =
    key === "confirmed" ? styles.badgeConfirmed :
    key === "cancelled" ? styles.badgeCancelled : styles.badgePending;
  return (
    <span className={`${styles.badge} ${cls}`}>
      <span className={styles.badgeDot} />
      {status || "—"}
    </span>
  );
};

const SortableTh = ({ label, sortKey, sort, onSort, align }) => {
  const active = sort.key === sortKey;
  return (
    <th
      className={`${styles.thSortable} ${align === "right" ? styles.thRight : ""} ${align === "center" ? styles.thCenter : ""}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && (
        <span className={styles.sortArrow}>
          {sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
        </span>
      )}
    </th>
  );
};

const InsuranceReport = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [criteria, setCriteria] = useState(INITIAL_CRITERIA);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "departureDate", dir: "desc" });
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const firstLoadRef = useRef(true);

  const fetchReport = useCallback(async (activeCriteria, { announce = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const res = await getInsuranceReport(activeCriteria);
      if (!res?.success) throw new Error(res?.message || "Failed to load insurance report");
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      if (Array.isArray(res.data?.companies)) setCompanies(res.data.companies);
      if (announce) {
        const n = res.data?.rows?.length || 0;
        showToast(`Report generated — ${n} polic${n === 1 ? "y" : "ies"}`, "success");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load insurance report";
      setError(msg);
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!firstLoadRef.current) return;
    firstLoadRef.current = false;
    fetchReport(INITIAL_CRITERIA, { announce: true });
  }, [fetchReport]);

  const setCriteriaField = (key) => (e) =>
    setCriteria((prev) => ({ ...prev, [key]: e.target.value }));

  const handleGenerate = () => {
    setPage(1);
    fetchReport(criteria, { announce: true });
  };

  const onSort = (key) => {
    setPage(1);
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.bookingReference || "").toLowerCase().includes(q) ||
        String(r.leadTraveller || r.leadTraveler || "").toLowerCase().includes(q) ||
        String(r.insuranceCompany || "").toLowerCase().includes(q) ||
        String(r.insuranceType || "").toLowerCase().includes(q) ||
        String(r.policyNumber || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => sortRows(filteredRows, sort.key, sort.dir), [filteredRows, sort]);

  const total = sortedRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = sortedRows.slice(startIdx, startIdx + pageSize);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + pageSize, total);

  useEffect(() => { setPage(1); }, [pageSize, search]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    const to = Math.min(pageCount, Math.max(1, currentPage - 2) + 4);
    for (let i = Math.max(1, to - 4); i <= to; i += 1) nums.push(i);
    return nums;
  }, [currentPage, pageCount]);

  // ── Export ──
  const EXPORT_HEADERS = [
    "Booking No", "Customer", "Insurance Company", "Insurance Type",
    "Policy Number", "Departure", "Travellers",
  ];
  const leadOf = (r) => r.leadTraveller || r.leadTraveler || r.customerName || "";
  const exportMatrix = () =>
    sortedRows.map((r) => [
      r.bookingReference || "", leadOf(r), r.insuranceCompany || "", r.insuranceType || "",
      r.policyNumber || "", formatReportDate(r.departureDate), r.paxLabel || String(r.travelers || 0),
    ]);

  const guardEmpty = () => {
    if (!sortedRows.length) {
      showToast("Nothing to export — no policies match the current report", "error");
      return true;
    }
    return false;
  };
  const stamp = new Date().toISOString().slice(0, 10);

  const handleExcel = () => {
    if (guardEmpty()) return;
    try {
      downloadExcel(`insurance-report-${stamp}.xls`, EXPORT_HEADERS, exportMatrix(), "Insurance Report");
      showToast("Excel exported", "success");
    } catch {
      showToast("Excel export failed", "error");
    }
  };

  const buildPrintHtml = () => {
    const kpis = KPI_META.map(
      (k) => `<div class="kpi"><div class="l">${k.label}</div><div class="v">${k.fmt(summary[k.key] ?? 0)}</div></div>`
    ).join("");
    const head = EXPORT_HEADERS.map((h) => `<th>${h}</th>`).join("");
    const body = exportMatrix()
      .map((r) => `<tr>${r.map((c) => `<td>${String(c).replace(/</g, "&lt;")}</td>`).join("")}</tr>`)
      .join("");
    return `<h1>Insurance Report</h1>
      <p class="sub">Overview of sold insurance policies · Generated ${new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })} · ${sortedRows.length} ${sortedRows.length === 1 ? "policy" : "policies"}</p>
      <div class="kpis">${kpis}</div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  };

  const handlePdf = () => {
    if (guardEmpty()) return;
    if (!printHtml(buildPrintHtml(), "Insurance Report")) {
      showToast("Allow pop-ups to export the PDF, then try again", "error");
      return;
    }
    showToast("Opening print dialog — choose “Save as PDF”", "success");
  };

  const handlePrint = () => {
    if (guardEmpty()) return;
    if (!printHtml(buildPrintHtml(), "Insurance Report")) {
      showToast("Allow pop-ups to print, then try again", "error");
      return;
    }
    showToast("Opening print dialog", "success");
  };

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerIcon}><ShieldCheck size={24} /></div>
        <div>
          <h1 className={styles.headerTitle}>Insurance Report</h1>
          <p className={styles.headerSub}>Overview of sold insurance policies and their associated customers.</p>
        </div>
      </div>

      {/* ── Report Criteria ── */}
      <div className={styles.card}>
        <div className={styles.criteriaHead}><Filter size={16} /> Report Criteria</div>
        <div className={styles.criteriaGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Departure Date From</span>
            <input type="date" className={styles.input} value={criteria.dateFrom} onChange={setCriteriaField("dateFrom")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Departure Date To</span>
            <input type="date" className={styles.input} value={criteria.dateTo} onChange={setCriteriaField("dateTo")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Insurance Company</span>
            <select className={styles.select} value={criteria.company} onChange={setCriteriaField("company")}>
              <option value="">All Companies</option>
              {companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Booking Status</span>
            <select className={styles.select} value={criteria.bookingStatus} onChange={setCriteriaField("bookingStatus")}>
              <option value="">All Statuses</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Completed">Completed</option>
              <option value="Draft">Draft</option>
            </select>
          </label>
          <div className={styles.generateWrap}>
            <button className={styles.generateBtn} onClick={handleGenerate} disabled={loading} type="button">
              {loading ? <Loader2 size={16} className={styles.spin} /> : <Search size={16} />}
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* ── Insurance Summary — KPI tiles ── */}
      <div className={styles.kpiGrid}>
        {KPI_META.map(({ key, label, Icon, cls, fmt }) => (
          <div key={key} className={`${styles.kpiCard} ${styles[cls]}`}>
            <span className={styles.kpiIcon}><Icon size={22} /></span>
            <div className={styles.kpiText}>
              <span className={styles.kpiLabel}>{label}</span>
              <span className={styles.kpiValue}>{loading ? "—" : fmt(summary[key] ?? 0)}</span>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className={styles.errorBox}><AlertCircle size={17} />{error}</div>
      )}

      {/* ── Results ── */}
      <div className={styles.card}>
        <div className={styles.resultsHead}>
          <div>
            <div className={styles.resultsTitle}><SlidersHorizontal size={16} /> Results</div>
            <div className={styles.resultsMeta}>
              {loading ? "Loading…"
                : total === 0 ? "No policies"
                : `Showing ${showingFrom} to ${showingTo} of ${total} ${total === 1 ? "policy" : "policies"}`}
            </div>
          </div>
          <div className={styles.searchBox}>
            <Search size={15} />
            <input
              className={styles.searchInput}
              placeholder="Search booking, customer, company, policy…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch("")} type="button"><X size={14} /></button>
            )}
          </div>
        </div>

        {loading ? (
          <div className={styles.skeletonWrap}>
            {[...Array(6)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
          </div>
        ) : total === 0 ? (
          <div className={styles.empty}>
            <ShieldCheck size={30} />
            <div className={styles.emptyTitle}>No insurance policies found</div>
            <div className={styles.emptySub}>
              {search ? "No policies match your search." : "Adjust the criteria above and click Generate Report."}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <SortableTh label="Booking No" sortKey="bookingReference" sort={sort} onSort={onSort} />
                    <SortableTh label="Customer" sortKey="leadTraveller" sort={sort} onSort={onSort} />
                    <SortableTh label="Insurance Company" sortKey="insuranceCompany" sort={sort} onSort={onSort} />
                    <SortableTh label="Insurance Type" sortKey="insuranceType" sort={sort} onSort={onSort} />
                    <SortableTh label="Policy Number" sortKey="policyNumber" sort={sort} onSort={onSort} />
                    <SortableTh label="Departure" sortKey="departureDate" sort={sort} onSort={onSort} />
                    <SortableTh label="Travellers" sortKey="travelers" sort={sort} onSort={onSort} align="center" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.productId}>
                      <td>
                        <span
                          className={styles.cellRef}
                          onClick={() => navigate(`/bookings/${r.bookingReference}`)}
                          title="Open booking"
                        >
                          {r.bookingReference || "—"}
                        </span>
                        <span className={styles.cellSub}><StatusBadge status={r.bookingStatus} /></span>
                      </td>
                      <td><span className={styles.cellPrimary}>{leadOf(r) || "—"}</span></td>
                      <td><span className={styles.cellPrimary}>{r.insuranceCompany || "—"}</span></td>
                      <td>{r.insuranceType || <span className={styles.cellMuted}>—</span>}</td>
                      <td>{r.policyNumber || <span className={styles.cellMuted}>—</span>}</td>
                      <td>{formatReportDate(r.departureDate)}</td>
                      <td className={styles.tdCenter}>
                        <span className={styles.paxChip}>{r.paxLabel || `${r.travelers || 0} pax`}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <div className={styles.pagination}>
              <div className={styles.rowsPer}>
                <span>Rows per page:</span>
                <select className={styles.rowsSelect} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className={styles.pager}>
                <button className={styles.pageBtn} onClick={() => setPage(1)} disabled={currentPage === 1}><ChevronsLeft size={15} /></button>
                <button className={styles.pageBtn} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={15} /></button>
                {pageNumbers.map((n) => (
                  <button key={n} className={`${styles.pageBtn} ${n === currentPage ? styles.pageBtnActive : ""}`} onClick={() => setPage(n)}>{n}</button>
                ))}
                <button className={styles.pageBtn} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={currentPage === pageCount}><ChevronRight size={15} /></button>
                <button className={styles.pageBtn} onClick={() => setPage(pageCount)} disabled={currentPage === pageCount}><ChevronsRight size={15} /></button>
              </div>
              <div className={styles.showingText}>Showing {showingFrom} to {showingTo} of {total}</div>
            </div>
          </>
        )}
      </div>

      {/* ── Export Report ── */}
      <div className={styles.exportCard}>
        <div className={styles.exportLeft}>
          <div className={styles.exportIcon}><FileText size={18} /></div>
          <div>
            <div className={styles.exportTitle}>Export Insurance Report</div>
            <div className={styles.exportSub}>Export the insurance report in your preferred format.</div>
          </div>
        </div>
        <div className={styles.exportBtns}>
          <button className={`${styles.exportBtn} ${styles.excelBtn}`} onClick={handleExcel} disabled={loading} type="button">
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button className={`${styles.exportBtn} ${styles.pdfBtn}`} onClick={handlePdf} disabled={loading} type="button">
            <FileText size={16} /> Export PDF
          </button>
          <button className={`${styles.exportBtn} ${styles.printBtn}`} onClick={handlePrint} disabled={loading} type="button">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceReport;
